import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    Box,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import React, {useMemo, useState} from "react";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    useAllChildResources
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {useAllServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import { Resource } from "crqlar-react-common/src/models/resource/resource.model";

interface Props {
    open: boolean
    onClose: () => void
    onConfirm: (
        updateOption: 'single' | 'linkedReservations' | 'subsequentLinkedReservations',
        changes?: {
            field: string
            oldValue: any
            newValue: any
        }[],
        eatPeriod?: 'breakfast' | 'lunch' | 'dinner' | 'all' | undefined
    ) => void
    reservation?: Reservation
    originalReservation?: Reservation
    restaurantResources?: Resource[]
}

const ReservationUpdateOptionDialog = (props: Props) => {
    const {t} = useLocalize()

    const allServices = useAllServices()
    const childrenResourcesPerRestaurant = useAllChildResources(props.restaurantResources?.map(r => r.id as string) || [])
    const allAgeCategories = useAllAgeCategories()

    const [changeBothBreakfastAndDinner, setChangeBothBreakfastAndDinner] = useState<boolean>(true)

    const allChildResourceIds = childrenResourcesPerRestaurant.map(r => r.id)

    const changes = useMemo(() => {
        if (!props.reservation || !props.originalReservation) return []

        const changedFields: Array<{field: string, oldValue: any, newValue: any}> = []

        // Define fields to check (customize based on what's relevant to show)
        const fieldsToCheck: Array<keyof Reservation> = [
            'serviceId',
            'expectedStartDate',
            'expectedEndDate',
            'actualStartDate',
            'actualEndDate',
            'status',
            'resourceIds',
            'profileIds',
            'guestCounts',
            'notes'
        ]

        fieldsToCheck.forEach(field => {
            const oldValue = props.originalReservation![field]
            const newValue = props.reservation![field]

            // Deep comparison for arrays and objects
            if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                changedFields.push({
                    field: field as string,
                    oldValue,
                    newValue
                })
            }
        })

        return changedFields
    }, [props.reservation, props.originalReservation])

    const getServiceName = (serviceId?: string): string => {
        if (!serviceId) return 'N/A'
        const service = allServices?.find(s => s.id === serviceId)
        return service?.name || serviceId
    }

    const getResourceNames = (resourceIds?: string[]): string => {
        if (!resourceIds || resourceIds.length === 0) return 'N/A'
        const names = resourceIds
            .map(id => {
                const resource = props.restaurantResources?.find(r => r.id === id)

                // Find restaurant
                return resource?.name ||
                    // Fallback to child resources
                    childrenResourcesPerRestaurant.find(r => r.id === id)?.name
            })
            .join(', ')
        return names
    }

    const getAgeCategoryName = (ageCategoryId?: string): string => {
        if (!ageCategoryId) return 'N/A'
        const ageCategory = allAgeCategories?.find(ac => ac.id === ageCategoryId)
        return ageCategory?.name || ageCategoryId
    }

    const formatValue = (field: string, value: any): string => {
        if (value === null || value === undefined) return 'N/A'

        // Handle specific field types
        switch (field) {
            case 'serviceId':
                return getServiceName(value)

            case 'resourceIds':
                return getResourceNames(value)

            case 'expectedStartDate':
            case 'expectedEndDate':
            case 'actualStartDate':
            case 'actualEndDate':
                return value instanceof Date
                    ? value.toLocaleString()
                    : new Date(value).toLocaleString()

            case 'guestCounts':
                if (Array.isArray(value)) {
                    return value.map(gc =>
                        `${getAgeCategoryName(gc.ageCategoryId)}: ${gc.count}`
                    ).join(', ')
                }
                return String(value)

            case 'notes':
                if (Array.isArray(value)) {
                    return `${value.length} note(s)`
                }
                return String(value)

            case 'profileIds':
                if (Array.isArray(value)) {
                    return `${value.length} profile(s)`
                }
                return String(value)

            case 'status':
                return t(value) // Assuming status values are translatable

            default:
                if (Array.isArray(value)) return `${value.length} items`
                if (typeof value === 'object') return JSON.stringify(value, null, 2)
                return String(value)
        }
    }

    const formatFieldName = (field: string): string => {
        // Convert camelCase to readable format
        switch (field) {
            case 'serviceId':
                return t('Service')
            case 'expectedStartDate':
                return t('Expected Start Date')
            case 'expectedEndDate':
                return t('Expected End Date')
            case 'actualStartDate':
                return t('Actual Start Date')
            case 'actualEndDate':
                return t('Actual End Date')
            case 'status':
                return t('Status')
            case 'resourceIds':
                return t('Resources')
            case 'profileIds':
                return t('Profiles')
            case 'guestCounts':
                return t('Guest Counts')
            case 'notes':
                return t('Notes')
            default:
                // Fallback: split camelCase
                return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
        }
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                id="alert-dialog-title"
                sx={{
                    textAlign: 'center'
                }}
            >
                {t("Update other reservations as well?")}
            </DialogTitle>

            <DialogContent>
                <Typography variant={'body1'} sx={{mb: 2}}>
                    {t('We have detected these changes')}:
                </Typography>

                {changes.length > 0 ? (
                    <Box sx={{mb: 3, maxHeight: '300px', overflow: 'auto'}}>
                        {changes.map((change, index) => (
                            <Box
                                key={index}
                                sx={{
                                    p: 2,
                                    mb: 1,
                                    bgcolor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'grey.300'
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {formatFieldName(change.field)}
                                </Typography>

                                <Typography variant="body1" color="text.secondary">
                                    {t('From')}: {formatValue(change.field, change.oldValue)}
                                </Typography>

                                <Typography variant="body1" color="primary">
                                    {t('To')}: {formatValue(change.field, change.newValue)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                        {t('No changes detected')}
                    </Typography>
                )}

                <Stack spacing={1} direction={'column'}>

                    <Button
                        variant={'outlined'}
                        onClick={() => props.onConfirm('single')}
                    >
                        {t('Update this reservation only')}
                    </Button>

                    {/*Hidden - product decision*/}
                    {/*<Button*/}
                    {/*    variant={'outlined'}*/}
                    {/*    // When updating also the linked reservations, we need to keep the changes and pass them along*/}
                    {/*    onClick={*/}
                    {/*        () => props.onConfirm(*/}
                    {/*            'linkedReservations',*/}
                    {/*            changes,*/}
                    {/*            props.reservation?.meta?.eatPeriod*/}
                    {/*        )*/}
                    {/*    }*/}
                    {/*>*/}
                    {/*    {t('Update this and all linked reservations')}*/}
                    {/*</Button>*/}

                    <Button
                        variant={'outlined'}
                        // When updating also the linked reservations, we need to keep the changes and pass them along
                        onClick={
                            () => props.onConfirm(
                                'subsequentLinkedReservations',
                                changes,
                                changeBothBreakfastAndDinner
                                    ? 'all'
                                    : props.reservation?.meta?.eatPeriod
                            )
                        }
                    >
                        {t('Update this and all subsequent linked reservations')}
                    </Button>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={changeBothBreakfastAndDinner}
                                onChange={() => setChangeBothBreakfastAndDinner(!changeBothBreakfastAndDinner)}
                            />
                        }
                        label={t('Change reservation for breakfast and dinner')}
                    />


                </Stack>

            </DialogContent>
        </Dialog>
    )
}

export default ReservationUpdateOptionDialog
